import { Swiper } from '@theme/mp-swiper';

class ProductMediaGallery extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.mainEl = this.querySelector('[data-swiper-main]');
    this.thumbsEl = this.querySelector('[data-swiper-thumbs]');
    this.mql = window.matchMedia('(min-width: 990px)');

    if (!this.mainEl) return;

    this.modalEl = document.querySelector(`#SwiperModal-${this.sectionId}`);

    this.syncThumbsHeight();
    this.initThumbs();
    this.initMain();
    this.slideToFeatured();
    this.bindVariantChange();
    this.bindResize();
    this.bindZoomModal();
  }

  syncThumbsHeight() {
    if (!this.thumbsEl) return;

    const update = () => {
      const mainHeight = this.querySelector('.swiper-gallery__main')?.offsetHeight;
      if (mainHeight) {
        this.style.setProperty('--gallery-main-height', `${mainHeight}px`);
      }
    };

    const firstImg = this.mainEl.querySelector('img');
    if (firstImg?.complete) {
      update();
    } else if (firstImg) {
      firstImg.addEventListener('load', update, { once: true });
    }

    this._syncThumbsHeight = update;
  }

  initThumbs() {
    if (!this.thumbsEl) return;

    const isDesktop = this.mql.matches;

    this.thumbsSwiper = new Swiper(this.thumbsEl, {
      direction: isDesktop ? 'vertical' : 'horizontal',
      spaceBetween: isDesktop ? 6 : 10,
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
      watchOverflow: true,
      navigation: {
        nextEl: this.thumbsEl.querySelector('.r23-swiper-button-next'),
        prevEl: this.thumbsEl.querySelector('.r23-swiper-button-prev'),
      },
    });
  }

  reinitSwipers() {
    const activeIndex = this.mainSwiper?.activeIndex || 0;

    if (this.mainSwiper) this.mainSwiper.destroy(true, true);
    if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);

    this.initThumbs();
    this.initMain();

    this.mainSwiper.slideTo(activeIndex, 0);
  }

  initMain() {
    const useFade = this.dataset.fade === 'true';

    this.mainSwiper = new Swiper(this.mainEl, {
      spaceBetween: 0,
      ...(useFade && { effect: 'fade', fadeEffect: { crossFade: true } }),
      thumbs: this.thumbsSwiper ? { swiper: this.thumbsSwiper } : undefined,
    });
  }

  slideToFeatured() {
    const featuredSlide = this.mainEl.querySelector('[data-featured]');
    if (!featuredSlide) return;

    const idx = parseInt(featuredSlide.dataset.slideIndex, 10);
    if (idx > 0) this.mainSwiper.slideTo(idx, 0);
  }

  bindVariantChange() {
    const productInfo = document.querySelector(`product-info[data-section="${this.sectionId}"]`);
    if (!productInfo) return;

    productInfo.addEventListener('variant:change', (e) => {
      const mediaId = e.detail?.featured_media_id;
      if (!mediaId) return;
      this.slideToMedia(mediaId);
    });
  }

  slideToMedia(mediaId) {
    const slides = this.mainEl.querySelectorAll('.swiper-slide');
    slides.forEach((slide, i) => {
      if (slide.dataset.mediaId == mediaId) {
        this.mainSwiper.slideTo(i);
      }
    });
  }

  bindResize() {
    if (!this.thumbsEl) return;

    let wasDesktop = this.mql.matches;
    let resizeTimer;

    window.addEventListener('resize', () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const isDesktop = this.mql.matches;

        if (isDesktop !== wasDesktop) {
          wasDesktop = isDesktop;
          this.reinitSwipers();
        }

        if (this._syncThumbsHeight) this._syncThumbsHeight();
      }, 250);
    });
  }

  bindZoomModal() {
    if (!this.modalEl) return;

    this.querySelector('[data-zoom-open]')?.addEventListener('click', () => this.openModal());
    this.modalEl.querySelector('[data-modal-close]')?.addEventListener('click', () => this.closeModal());

    this.modalEl.addEventListener('click', (e) => {
      if (e.target === this.modalEl) this.closeModal();
    });

    this._handleEsc = (e) => {
      if (e.key === 'Escape') this.closeModal();
    };
  }

  openModal() {
    const activeIndex = this.mainSwiper?.activeIndex || 0;

    // Map main slider index to modal index (modal only has images)
    const mainSlides = [...this.mainEl.querySelectorAll('.swiper-slide')];
    const activeMediaId = mainSlides[activeIndex]?.dataset.mediaId;
    let modalIndex = 0;
    const modalSlides = [...this.modalEl.querySelector('[data-modal-main]').querySelectorAll('.swiper-slide')];
    modalSlides.forEach((slide, i) => {
      if (slide.dataset.mediaId === activeMediaId) modalIndex = i;
    });

    this.modalEl.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', this._handleEsc);

    if (!this.modalMainSwiper) {
      this.initModalSwipers(modalIndex);
    } else {
      this.modalMainSwiper.slideTo(modalIndex, 0);
    }
  }

  closeModal() {
    this.modalEl.classList.remove('is-open');
    document.body.style.overflow = '';
    document.removeEventListener('keydown', this._handleEsc);
  }

  initModalSwipers(initialSlide = 0) {
    const modalThumbsEl = this.modalEl.querySelector('[data-modal-thumbs]');
    const modalMainEl = this.modalEl.querySelector('[data-modal-main]');

    if (modalThumbsEl) {
      this.modalThumbsSwiper = new Swiper(modalThumbsEl, {
        direction: 'horizontal',
        spaceBetween: 8,
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
      });
    }

    const useFade = this.dataset.fade === 'true';

    this.modalMainSwiper = new Swiper(modalMainEl, {
      spaceBetween: 0,
      initialSlide,
      ...(useFade && { effect: 'fade', fadeEffect: { crossFade: true } }),
      thumbs: this.modalThumbsSwiper ? { swiper: this.modalThumbsSwiper } : undefined,
    });
  }

  disconnectedCallback() {
    if (this.mainSwiper) this.mainSwiper.destroy(true, true);
    if (this.thumbsSwiper) this.thumbsSwiper.destroy(true, true);
    if (this.modalMainSwiper) this.modalMainSwiper.destroy(true, true);
    if (this.modalThumbsSwiper) this.modalThumbsSwiper.destroy(true, true);
    document.removeEventListener('keydown', this._handleEsc);
  }
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
